import { getButtonAttribution } from 'site/Renderer/utils/getButtonAttribution'

import { isPublicButton } from 'shared/utils'
import {
  ActionButton,
  IndividualLinkButton,
  IndividualLinkButtonEntry,
  IndividualPublicButton,
  LinkActionButton,
  ModalActionButton,
  ProtectedActionButton,
  PublicButtonPropsWithType,
  SummaryDetailsCarouselButtonAttribution
} from './SummaryDetailsCarouselContainer.types'

const SINGLE_BUTTON_TYPES = ['modal', 'link', 'protected', 'public'] as const

type SingleButtonType = (typeof SINGLE_BUTTON_TYPES)[number]

type MaybeTyped = { type?: string } | object

type ButtonEntry = IndividualLinkButtonEntry | PublicButtonPropsWithType

type SingleButton =
  | ModalActionButton
  | LinkActionButton
  | ProtectedActionButton
  | PublicButtonPropsWithType

function hasTypedProperty(button: unknown): button is MaybeTyped {
  return typeof button === 'object' && button !== null
}

function isSingleButtonConfig(
  button: ActionButton | undefined
): button is SingleButton {
  if (!button || !hasTypedProperty(button)) return false
  const type = button.type
  return (
    typeof type === 'string' &&
    SINGLE_BUTTON_TYPES.includes(type as SingleButtonType)
  )
}

/**
 * Pattern: { [itemUuid]: { link, label, style? } } or { [itemUuid]: { type: 'public', children, ... } }.
 * Returns undefined when `button` is a single-button config or when there is no entry for `itemUuid`.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function getButtonEntryForItem(
  button: ActionButton | undefined,
  itemUuid: string
): ButtonEntry | undefined {
  if (!button || !isRecord(button)) return undefined
  if (isSingleButtonConfig(button)) return undefined

  const entry = button[itemUuid] as unknown
  return isRecord(entry) ? (entry as unknown as ButtonEntry) : undefined
}

export function isModalButton(
  button: ActionButton | undefined
): button is ModalActionButton {
  return hasTypedProperty(button) && button.type === 'modal'
}

export function isLinkButton(
  button: ActionButton | undefined
): button is LinkActionButton {
  return hasTypedProperty(button) && button.type === 'link'
}

export function isProtectedButton(
  button: ActionButton | undefined
): button is ProtectedActionButton {
  return hasTypedProperty(button) && button.type === 'protected'
}

function isIndividualPublicButton(
  button: ActionButton | undefined
): button is IndividualPublicButton {
  return isRecord(button)
}

function isIndividualLinkButton(
  button: ActionButton | undefined
): button is IndividualLinkButton {
  return isRecord(button)
}

const NULL_ATTRIBUTION: SummaryDetailsCarouselButtonAttribution = {
  type: null
}

export function getSummaryDetailsCarouselButtonAttribution(
  button: ActionButton | undefined,
  itemUuid: string
): SummaryDetailsCarouselButtonAttribution {
  return getButtonAttribution<SummaryDetailsCarouselButtonAttribution>(
    button,
    itemUuid,
    {
      singleMatchers: [
        {
          match: (b) => isModalButton(b as ActionButton),
          attribution: (b) => ({ type: 'modal', props: b as ModalActionButton })
        },
        {
          match: (b) => isLinkButton(b as ActionButton),
          attribution: (b) => ({ type: 'link', props: b as LinkActionButton })
        },
        {
          match: (b) => isProtectedButton(b as ActionButton),
          attribution: (b) => ({
            type: 'protected',
            props: b as ProtectedActionButton
          })
        },
        {
          match: (b) => isPublicButton(b as ActionButton),
          attribution: (b) => ({
            type: 'public',
            props: b as PublicButtonPropsWithType
          })
        }
      ],
      getPerItemAttribution: (b, id) => {
        const entry = getButtonEntryForItem(b as ActionButton, id)
        if (!entry) return null
        if ('type' in entry && entry.type === 'public') {
          if (isIndividualPublicButton(b as ActionButton)) {
            return {
              type: 'individualPublic',
              props: (b as IndividualPublicButton)[id]
            }
          }
        }
        if ('link' in entry && typeof entry.link === 'object') {
          if (isIndividualLinkButton(b as ActionButton)) {
            return {
              type: 'individualLink',
              props: (b as IndividualLinkButton)[id]
            }
          }
        }
        return null
      },
      nullAttribution: NULL_ATTRIBUTION
    }
  )
}
